import { s } from '@/theme/tokens';
import { AdapterDayjs, LocalizationProvider, MuiDatePicker } from './datePickers';
import { FormField } from './FormField';
import type { SxProps, Theme } from './types';
import type { Dayjs } from 'dayjs';
import type { ReactNode } from 'react';
import 'dayjs/locale/en-gb';

export type DatePickerProps = {
  label?: ReactNode;
  inlineLabel?: boolean;
  helpText?: ReactNode;
  errorText?: ReactNode;
  required?: boolean;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  placeholder?: string;
  textFieldSx?: SxProps<Theme>;
  openPickerButtonSx?: SxProps<Theme>;
  locale?: string;
  format?: string;
};

export const DatePicker = ({
  label,
  inlineLabel = false,
  helpText,
  errorText,
  required,
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  placeholder,
  textFieldSx,
  openPickerButtonSx,
  locale,
  format,
}: DatePickerProps): JSX.Element => (
  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale ?? 'en-gb'}>
    <FormField
      label={inlineLabel ? undefined : label}
      helpText={helpText}
      errorText={errorText}
      required={required}
    >
      {(props) => {
        const { label: _label, labelId: _labelId, ...fieldProps } = props;
        const resolvedLocale = locale ?? 'en-gb';
        const resolvedFormat =
          format ??
          (resolvedLocale.toLowerCase().endsWith('gb')
            ? 'DD/MM/YYYY'
            : resolvedLocale.toLowerCase().startsWith('en')
              ? 'MM/DD/YYYY'
              : undefined);
        return (
          <MuiDatePicker
            value={value}
            onChange={onChange}
            minDate={minDate}
            maxDate={maxDate}
            disabled={disabled}
            format={resolvedFormat}
            slotProps={{
              textField: {
                ...fieldProps,
                fullWidth: true,
                required,
                error: Boolean(errorText),
                size: 'small',
                label: inlineLabel ? label : undefined,
                helperText: undefined,
                placeholder,
                InputLabelProps: inlineLabel ? { shrink: true } : undefined,
                inputProps: {
                  ...(fieldProps as { inputProps?: Record<string, unknown> }).inputProps,
                  placeholder,
                },
                sx: [
                  {
                    '& .MuiPickersInputBase-root': { paddingLeft: s('sm') },
                    '& .MuiPickersOutlinedInput-root': { paddingLeft: s('sm') },
                    '& .MuiPickersInputBase-sectionsContainer': { paddingLeft: s('sm') },
                    '& .MuiPickersSectionList-root': { paddingLeft: s('sm') },
                    '& .MuiPickersInputBase-input': {
                      paddingLeft: s('sm'),
                      fontSize: (theme: Theme) => theme.typography.body2.fontSize,
                      lineHeight: (theme: Theme) => theme.typography.body2.lineHeight,
                    },
                    '& .MuiPickersInputBase-sectionContent': {
                      fontSize: (theme: Theme) => theme.typography.body2.fontSize,
                      lineHeight: (theme: Theme) => theme.typography.body2.lineHeight,
                    },
                  },
                  ...(textFieldSx
                    ? Array.isArray(textFieldSx)
                      ? textFieldSx
                      : [textFieldSx]
                    : []),
                ],
              },
              openPickerButton: {
                size: 'small',
                sx: [
                  {
                    pl: 0,
                    ml: `-${s('sm')}`,
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.1rem',
                      mr: s('xs'),
                    },
                  },
                  ...(openPickerButtonSx
                    ? Array.isArray(openPickerButtonSx)
                      ? openPickerButtonSx
                      : [openPickerButtonSx]
                    : []),
                ],
              },
            }}
          />
        );
      }}
    </FormField>
  </LocalizationProvider>
);
